import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, ChevronsUpDown, Plus, Check } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function CompanySwitcher() {
  const { t } = useLang();
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [create, setCreate] = useState(false);
  const [name, setName] = useState("");
  const load = () => api.get("/companies").then((r) => setData(r.data));
  useEffect(() => { load(); }, [user?.id]);
  if (!data) return null;
  const switchTo = async (id) => {
    try {
      const { data: u } = await api.post(`/companies/${id}/switch`);
      setUser(u);
      setOpen(false);
      toast.success(t("companySwitched"));
      nav("/dashboard");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/companies", { company_name: name });
      const { data: u } = await api.get("/auth/me");
      setUser(u);
      setCreate(false); setOpen(false); setName("");
      toast.success(t("companyCreated"));
      nav("/dashboard");
    } catch (err) {
      const code = err?.response?.data?.detail?.code;
      toast.error(code === "plan_business" ? t("planBusinessCompanies") : code === "max_companies" ? t("maxCompanies") : errMsg(err));
    }
  };
  return (
    <div className="px-3 pb-2">
      <button data-testid="company-switcher-btn" onClick={() => setOpen(!open)} className="flex w-full items-center gap-2 rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-start text-sm hover:bg-white/[0.08]">
        <Building2 className="h-4 w-4 text-[#00C462]" />
        <span className="flex-1 truncate font-semibold" data-testid="current-company-name">{user?.company_name || user?.name}</span>
        <ChevronsUpDown className="h-4 w-4 text-white/40" />
      </button>
      {open && (
        <div data-testid="company-switcher-menu" className="mt-1 space-y-0.5 rounded-lg border border-white/10 bg-[#101a2e] p-1">
          {data.companies.map((c) => (
            <button key={c.id} data-testid={`company-option-${c.id}`} onClick={() => !c.current && switchTo(c.id)} className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-start text-sm ${c.current ? "text-white" : "text-white/70 hover:bg-white/[0.06]"}`}>
              <span className="flex-1 truncate">{c.company_name}</span>{c.current && <Check className="h-3.5 w-3.5 text-[#00C462]" />}
            </button>
          ))}
          {data.companies.length < data.max && (
            <button data-testid="company-add-btn" onClick={() => setCreate(true)} className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-start text-xs font-semibold text-[#F0D77A] hover:bg-white/[0.06]"><Plus className="h-3.5 w-3.5" />{t("addCompany")} ({data.companies.length}/{data.max})</button>
          )}
        </div>
      )}
      <Dialog open={create} onOpenChange={setCreate}>
        <DialogContent data-testid="company-create-dialog">
          <DialogHeader><DialogTitle>{t("addCompany")}</DialogTitle><DialogDescription>{t("addCompanyText")}</DialogDescription></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <Input data-testid="company-name-input" required minLength={2} value={name} onChange={(e) => setName(e.target.value)} placeholder={t("companyName")} />
            <Button data-testid="company-create-submit" className="w-full bg-[#007A3D] hover:bg-[#005E2E]">{t("create")}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
